import { isDynamicImportError } from "./isDynamicImportError";

const RELOAD_ATTEMPTED_KEY = "bootstrap-reload-attempted";

/**
 * Handles errors from the dynamic `import("./bootstrap")` in the app entry point.
 * If the bootstrap module itself fails to load (e.g. stale chunks after deployment),
 * the chunk error recovery registered inside bootstrap never runs. This function
 * fills that gap by reloading the page once.
 *
 * Use as: `void import("./bootstrap").catch(handleBootstrapError);`
 */
export function handleBootstrapError(
  error: unknown,
  options: {
    reload?: () => void;
    storage?: Pick<Storage, "getItem" | "setItem" | "removeItem">;
  } = {}
): void {
  const { reload = () => window.location.reload(), storage = sessionStorage } =
    options;

  const message = error instanceof Error ? error.message : String(error ?? "");

  if (!isDynamicImportError(message)) {
    throw error;
  }

  if (storage.getItem(RELOAD_ATTEMPTED_KEY)) {
    return;
  }

  storage.setItem(RELOAD_ATTEMPTED_KEY, "true");
  reload();
}

/**
 * Clears the bootstrap reload flag. Call this after bootstrap loads successfully
 * so that future bootstrap errors can trigger a reload.
 */
export function clearBootstrapReloadFlag(
  storage: Pick<Storage, "removeItem"> = sessionStorage
): void {
  storage.removeItem(RELOAD_ATTEMPTED_KEY);
}
